import { useTranslation } from "react-i18next"

import type { ChatSelectedFileItem } from "./chat-selected-files-utils"
import type { ParsedSupportingContentItem } from "./supporting-content-utils"

import { ExternalLink, Eye, FileText, Loader2, Trash2, X } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { cn } from "@/lib/utils"

import { CitationIcon } from "./citation-icon"
import { getCitationType } from "./citation.utils"
import { ContextSearchMatchPill } from "./context-search-match-pill"
import { renderFileIcon } from "./file-picker/utils"
import { WebSourceIcon } from "./web-source-icon"
import { useCitationPreview } from "../contexts/use-citation-preview"
import { getContextSearchMatch } from "../utils/context-search-match"
import { isSafeExternalUrl, openSafeExternalUrl } from "../utils/safe-external-url"
import { resolveWebCitationDisplay } from "../utils/web-citation-display"

type ChatSelectedFilesContextPanelProps = {
  items: ChatSelectedFileItem[]
  answerReferences?: ParsedSupportingContentItem[]
  citationSection?: string
  inline?: boolean
  removingFileName?: string | null
  searchQuery?: string
  /**
   * Current chat session id. Session-kind items (`item.kind === "session"`)
   * preview through it instead of `citationSection` — their `previewCitation`
   * is a bare filename (no `index_id:` prefix), and the backend content route
   * only knows how to resolve that shape against a session id (issue #2529).
   */
  sessionId?: string
  onOpenReference?: (url: string) => void
  onPreviewFile?: (
    file: File,
    options?: { backLabel?: string; preserveSidebarStack?: boolean }
  ) => void
  onRemoveFile?: (file: File | string) => void
  onRemoveDocument?: (documentId: string) => void
}

const GROUPS = [
  {
    kind: "document",
    labelKey: "questioninput.selected_file_group_documents",
    defaultLabel: "Documents",
  },
  {
    kind: "session",
    labelKey: "questioninput.selected_file_group_session",
    defaultLabel: "Session files",
  },
  {
    kind: "indexed",
    labelKey: "questioninput.selected_file_group_indexed",
    defaultLabel: "Indexed files",
  },
  {
    kind: "file",
    labelKey: "questioninput.selected_file_group_uploaded",
    defaultLabel: "Uploaded files",
  },
] satisfies Array<{
  kind: ChatSelectedFileItem["kind"]
  labelKey: string
  defaultLabel: string
}>

const CONTEXT_ROW_CLASS_NAME =
  "bg-card hover:bg-muted/50 hover:border-primary/30 focus-within:bg-muted/40 focus-within:border-primary/40 focus-within:ring-ring/20 flex min-w-0 items-center gap-2 rounded-md border px-2.5 py-2 transition-[background-color,border-color,box-shadow] focus-within:ring-1"
const CONTEXT_SECTION_TITLE_CLASS_NAME =
  "text-muted-foreground text-xs font-semibold tracking-wider uppercase"
const CONTEXT_COUNT_BADGE_CLASS_NAME =
  "border-border bg-muted/40 text-muted-foreground shrink-0 rounded-md border px-2 py-0.5 text-xs font-semibold"

function isRemovingItem(item: ChatSelectedFileItem, removingFileName?: string | null): boolean {
  if (!removingFileName) return false
  return item.kind !== "document" && item.label === removingFileName
}

function getItemIcon(item: ChatSelectedFileItem) {
  if (item.kind === "document") {
    return <FileText aria-hidden className="text-muted-foreground mr-1 inline size-4" />
  }
  return renderFileIcon(item.label, item.file?.type)
}

function getReferenceIcon(reference: ParsedSupportingContentItem) {
  if (reference.referenceKind === "web" && reference.link) {
    return <WebSourceIcon hostname={resolveWebCitationDisplay(reference.link).hostname} />
  }
  if (reference.citationId) {
    return (
      <CitationIcon
        citation={reference.citationId}
        className="text-muted-foreground size-4 shrink-0"
        type={getCitationType(reference.citationId)}
      />
    )
  }
  return <FileText aria-hidden className="text-muted-foreground size-4 shrink-0" />
}

function getReferenceMetadata(reference: ParsedSupportingContentItem): string {
  if (reference.referenceKind === "web") {
    return reference.subtitle || reference.link || reference.sourceLabel
  }
  return [reference.sourceLabel, reference.subtitle].filter(Boolean).join(" - ")
}

export function ChatSelectedFilesContextPanel({
  answerReferences = [],
  inline = false,
  items,
  citationSection,
  removingFileName,
  searchQuery = "",
  sessionId,
  onOpenReference,
  onPreviewFile,
  onRemoveDocument,
  onRemoveFile,
}: ChatSelectedFilesContextPanelProps) {
  const { t } = useTranslation()
  const { openPreview } = useCitationPreview()
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()

  const matchesSearch = (...values: Array<string | undefined>) => {
    if (!normalizedSearchQuery) return true
    return values.some((value) => value?.toLowerCase().includes(normalizedSearchQuery))
  }

  const visibleItems = items.filter((item) =>
    matchesSearch(item.label, item.detail, item.kind, item.searchableText)
  )
  const visibleAnswerReferences = answerReferences.filter((reference) =>
    normalizedSearchQuery
      ? Boolean(
          getContextSearchMatch(searchQuery, [
            { label: "title", value: reference.title },
            { label: "source", value: reference.sourceLabel },
            { label: "metadata", value: reference.subtitle },
            { label: "url", value: reference.link },
            { label: "content", value: reference.contentHtml },
            { label: "type", value: reference.referenceKind },
          ])
        )
      : true
  )

  const handleRemove = (item: ChatSelectedFileItem) => {
    if (item.kind === "document") {
      onRemoveDocument?.(String(item.removeValue))
      return
    }
    onRemoveFile?.(item.removeValue)
  }

  const handleClearAll = () => {
    for (const item of visibleItems) {
      handleRemove(item)
    }
  }

  // Session-kind previewCitation values are bare filenames (no "index_id:"
  // prefix) — the content route only resolves that shape against a session
  // id, not the library `citationSection` (issue #2529).
  const resolvePreviewSection = (item: ChatSelectedFileItem): string | undefined =>
    item.kind === "session" && sessionId ? sessionId : citationSection

  const handlePreview = (item: ChatSelectedFileItem) => {
    const previewOptions = {
      backLabel: t("questioninput.back_to_context", { defaultValue: "Back to context" }),
      preserveSidebarStack: true,
    }
    if (item.file) {
      onPreviewFile?.(item.file, previewOptions)
      return
    }
    const previewSection = resolvePreviewSection(item)
    if (item.previewCitation && previewSection) {
      openPreview(item.previewCitation, previewSection, previewOptions)
    }
  }

  const handleReferencePreview = (reference: ParsedSupportingContentItem) => {
    if (!reference.citationId || !citationSection) return
    openPreview(reference.citationId, citationSection, {
      backLabel: t("questioninput.back_to_context", { defaultValue: "Back to context" }),
      preserveSidebarStack: true,
    })
  }

  const handleOpenReference = (url: string) => {
    if (!isSafeExternalUrl(url)) return
    if (onOpenReference) {
      onOpenReference(url)
      return
    }
    openSafeExternalUrl(url)
  }

  if (visibleItems.length === 0 && visibleAnswerReferences.length === 0) {
    return null
  }

  return (
    <section
      className={cn(
        "flex min-h-0 flex-col",
        inline ? "gap-4" : "max-h-[45vh] shrink-0 gap-3 p-3 sm:max-h-96"
      )}
    >
      {visibleItems.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className={CONTEXT_SECTION_TITLE_CLASS_NAME}>
              {t("questioninput.files_in_context", { defaultValue: "Files in context" })}
            </h3>
            <p className="text-muted-foreground text-xs">
              {t("questioninput.files_in_context_description", {
                defaultValue: "Attached to the next message.",
              })}
            </p>
          </div>
          <button
            className={cn(buttonVariants({ size: "sm", variant: "ghost" }), "h-8 shrink-0 gap-1")}
            type="button"
            onClick={handleClearAll}
          >
            <Trash2 aria-hidden className="size-3.5" />
            {normalizedSearchQuery
              ? t("questioninput.clear_visible_files", { defaultValue: "Clear shown" })
              : t("questioninput.clear_all", { defaultValue: "Clear all" })}
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto pr-1">
        {GROUPS.map((group) => {
          const groupItems = visibleItems.filter((item) => item.kind === group.kind)
          if (groupItems.length === 0) return null

          return (
            <section key={group.kind} className="flex flex-col gap-2">
              <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t(group.labelKey, { defaultValue: group.defaultLabel })}
              </h4>
              <div className="flex flex-col gap-2">
                {groupItems.map((item) => {
                  const canPreview = Boolean(
                    (item.previewCitation && resolvePreviewSection(item)) ||
                    (item.file && onPreviewFile)
                  )
                  const isRemoving = isRemovingItem(item, removingFileName)
                  const searchMatch = getContextSearchMatch(searchQuery, [
                    { label: "name", value: item.label },
                    { label: "metadata", value: item.detail },
                    { label: "content", value: item.searchableText },
                    { label: "type", value: item.kind },
                  ])

                  return (
                    <div
                      key={item.key}
                      className={cn(CONTEXT_ROW_CLASS_NAME, isRemoving && "opacity-60")}
                    >
                      <div className="shrink-0">{getItemIcon(item)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
                            {item.label}
                          </div>
                          {searchMatch ? <ContextSearchMatchPill match={searchMatch} /> : null}
                        </div>
                        <div className="text-muted-foreground truncate text-xs">{item.detail}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {canPreview ? (
                          <button
                            type="button"
                            aria-label={t("questioninput.preview_file", {
                              defaultValue: "Preview {{name}}",
                              name: item.label,
                            })}
                            className={cn(
                              buttonVariants({ size: "icon", variant: "ghost" }),
                              "size-8"
                            )}
                            onClick={() => handlePreview(item)}
                          >
                            <Eye aria-hidden className="size-4" />
                          </button>
                        ) : null}
                        <button
                          disabled={isRemoving}
                          type="button"
                          aria-label={t("questioninput.remove_named_file", {
                            defaultValue: "Remove {{name}}",
                            name: item.label,
                          })}
                          className={cn(
                            buttonVariants({ size: "icon", variant: "ghost" }),
                            "hover:bg-destructive/10 hover:text-destructive size-8"
                          )}
                          onClick={() => handleRemove(item)}
                        >
                          {isRemoving ? (
                            <Loader2 aria-hidden className="size-4 animate-spin" />
                          ) : (
                            <X aria-hidden className="size-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )
        })}
        {visibleAnswerReferences.length > 0 && (
          <section className={cn("flex flex-col gap-2", !inline && "border-t pt-3")}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h4 className={CONTEXT_SECTION_TITLE_CLASS_NAME}>
                  {t("questioninput.references_from_answer", {
                    defaultValue: "References from answer",
                  })}
                </h4>
                <p className="text-muted-foreground text-xs">
                  {t("questioninput.references_from_answer_description", {
                    defaultValue: "Sources used or discovered.",
                  })}
                </p>
              </div>
              <span className={CONTEXT_COUNT_BADGE_CLASS_NAME}>
                {visibleAnswerReferences.length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {visibleAnswerReferences.map((reference) => {
                const canOpen = isSafeExternalUrl(reference.link)
                const canPreview = Boolean(!canOpen && reference.citationId && citationSection)
                const referenceMetadata = getReferenceMetadata(reference)
                const searchMatch = getContextSearchMatch(searchQuery, [
                  { label: "title", value: reference.title },
                  { label: "source", value: reference.sourceLabel },
                  { label: "metadata", value: reference.subtitle },
                  { label: "url", value: reference.link },
                  { label: "content", value: reference.contentHtml },
                  { label: "type", value: reference.referenceKind },
                ])
                const actionLabel = canOpen
                  ? t("questioninput.open_reference", {
                      defaultValue: "Open {{name}}",
                      name: reference.title,
                    })
                  : t("questioninput.preview_file", {
                      defaultValue: "Preview {{name}}",
                      name: reference.title,
                    })

                return (
                  <div
                    key={`${reference.referenceKind}-${reference.citationId ?? reference.link ?? reference.title}`}
                    className={CONTEXT_ROW_CLASS_NAME}
                  >
                    {getReferenceIcon(reference)}
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
                          {reference.title}
                        </div>
                        {searchMatch ? <ContextSearchMatchPill match={searchMatch} /> : null}
                      </div>
                      {referenceMetadata ? (
                        <div className="text-muted-foreground truncate text-xs">
                          {referenceMetadata}
                        </div>
                      ) : null}
                    </div>
                    {(canPreview || canOpen) && (
                      <button
                        aria-label={actionLabel}
                        className={cn(buttonVariants({ size: "icon", variant: "ghost" }), "size-8")}
                        type="button"
                        onClick={() => {
                          if (canOpen && reference.link) {
                            handleOpenReference(reference.link)
                          } else if (canPreview) {
                            handleReferencePreview(reference)
                          }
                        }}
                      >
                        {canOpen ? (
                          <ExternalLink aria-hidden className="size-4" />
                        ) : (
                          <Eye aria-hidden className="size-4" />
                        )}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )}
      </div>
    </section>
  )
}
